import React, { useState, useEffect, useMemo } from "react";
import {
  FileText,
  Clock,
  UserCheck,
  CheckCircle,
  AlertCircle,
  XCircle,
  BarChart3,
  PieChart as PieIcon,
} from "lucide-react";
import {
  getDashboardStats,
  DashboardStatsOutput,
} from "../../../server/AdminStartup-dashboard/requests";
import { getSubmissions } from "../../../server/StartupAdmins CRUD/Requests";
import { GetSubmissionsOutput } from "../../../server/StartupAdmins CRUD/OutputsTypes";

// Palette raffinée
const COLORS = [
  "#4361EE",
  "#3A0CA3",
  "#F72585",
  "#4CC9F0",
  "#4895EF",
  "#560BAD",
  "#7209B7",
  "#B5179E",
  "#F15BB5",
  "#FEE440",
  "#00BBF9",
  "#00F5D4",
];

// Carte statistique
interface StatCardProps {
  title: string;
  value: number;
  color: string;
  icon: React.ElementType;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  color,
  icon: Icon,
}) => (
  <div
    className={`flex flex-col bg-white rounded-lg shadow-md p-6 transition-all duration-300 border-l-4 ${color} hover:shadow-lg`}
  >
    <div className="flex justify-between items-center mb-3">
      <div className="text-gray-500 text-sm font-medium">{title}</div>
      <Icon className="text-gray-400" size={18} />
    </div>
    <div className="text-3xl font-bold">
      {Number.isFinite(value) ? value : "Néant"}
    </div>
  </div>
);

// Interfaces pour les graphiques
interface ChartDataProps {
  data: number[];
  labels: string[];
}

// Camembert SVG
const PieChart: React.FC<ChartDataProps> = ({ data, labels }) => {
  const total = data.reduce((a: number, b: number) => a + b, 0);
  let cumulative = 0;
  return (
    <div className="relative">
      <svg viewBox="0 0 100 100" className="w-56 h-56">
        <circle cx="50" cy="50" r="30" fill="white" />
        {data.map((value: number, i: number) => {
          const percentage = value / total;
          const startAngle = (cumulative / total) * 2 * Math.PI;
          const endAngle = ((cumulative + value) / total) * 2 * Math.PI;
          const x1 = 50 + 40 * Math.sin(startAngle);
          const y1 = 50 - 40 * Math.cos(startAngle);
          const x2 = 50 + 40 * Math.sin(endAngle);
          const y2 = 50 - 40 * Math.cos(endAngle);
          const largeArc = percentage > 0.5 ? 1 : 0;
          const pathData = `M50,50 L${x1},${y1} A40,40 0 ${largeArc} 1 ${x2},${y2} Z`;
          cumulative += value;
          return (
            <path
              key={i}
              d={pathData}
              fill={COLORS[i % COLORS.length]}
              stroke="white"
              strokeWidth="1"
              className="transition-all duration-500 hover:opacity-90"
            />
          );
        })}
        <text
          x="50"
          y="48"
          textAnchor="middle"
          className="text-2xl font-bold"
          fontSize="10"
        >
          {total}
        </text>
        <text
          x="50"
          y="58"
          textAnchor="middle"
          className="text-xs text-gray-400"
          fontSize="6"
        >
          Total
        </text>
      </svg>
    </div>
  );
};

// Légende camembert
const PieLegend: React.FC<ChartDataProps> = ({ data, labels }) => {
  const total = data.reduce((a: number, b: number) => a + b, 0);
  return (
    <div className="grid grid-cols-2 gap-3 mt-4">
      {labels.map((label: string, i: number) => (
        <div
          key={label}
          className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-50 transition-all"
        >
          <span
            className="inline-block w-4 h-4 rounded-full"
            style={{ backgroundColor: COLORS[i % COLORS.length] }}
          ></span>
          <div className="flex flex-col">
            <span className="font-medium text-sm">{label}</span>
            <div className="flex items-center space-x-1">
              <span className="text-gray-700 font-semibold">
                {Number.isFinite(data[i]) ? data[i] : "Néant"}
              </span>
              <span className="text-gray-400 text-xs">
                {total > 0 && Number.isFinite(data[i])
                  ? `(${((data[i] / total) * 100).toFixed(0)}%)`
                  : "(Néant)"}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Bar chart SVG adapté
const BarChart: React.FC<ChartDataProps> = ({ data, labels }) => {
  const max = Math.max(...data, 1);
  const barColors = [
    "#FFC107",
    "#FF9800",
    "#4CAF50",
    "#F44336",
    "#3A0CA3",
    "#00BBF9",
  ];
  return (
    <div className="w-full flex flex-col items-center">
      <svg viewBox={`0 0 ${data.length * 45 + 20} 110`} className="w-full h-52">
        {[0, 1, 2, 3, 4].map((i: number) => (
          <line
            key={`grid-${i}`}
            x1="0"
            y1={80 - i * 20}
            x2={data.length * 45 + 20}
            y2={80 - i * 20}
            stroke="#f0f0f0"
            strokeWidth="1"
          />
        ))}
        {data.map((v: number, i: number) => (
          <g key={i} className="transition-all duration-500">
            <rect
              x={15 + i * 45}
              y={80 - (v / max) * 70}
              width={30}
              height={(v / max) * 70}
              fill={barColors[i % barColors.length]}
              rx={4}
              className="transition-all duration-500 hover:opacity-90"
            />
            <text
              x={30 + i * 45}
              y={95}
              textAnchor="middle"
              fontSize="8"
              fill="#555"
              fontWeight="500"
            >
              {labels[i]}
            </text>
            <text
              x={30 + i * 45}
              y={80 - (v / max) * 70 - 8}
              textAnchor="middle"
              fontSize="10"
              fill="#333"
              fontWeight="bold"
            >
              {Number.isFinite(v) ? v : "Néant"}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

const Dashboard = () => {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [stats, setStats] = useState<DashboardStatsOutput | null>(null);
  const [recentSubmissions, setRecentSubmissions] = useState<
    GetSubmissionsOutput[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // List of years for selection (last 5 years)
  const availableYears = useMemo(() => {
    const year = currentDate.getFullYear();
    return Array.from({ length: 5 }, (_, i) => year - i);
  }, [currentDate]);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const { status, body } = await getDashboardStats({
        month: selectedMonth,
        year: selectedYear,
      });

      if (status === 200) {
        setStats(body as DashboardStatsOutput);
      } else {
        setError(
          "message" in body ? body.message : "Erreur lors du chargement",
        );
      }
    } catch (err) {
      setError("Erreur lors du chargement des données");
    }
    setLoading(false);
  };

  const fetchRecentSubmissions = async () => {
    try {
      const { status, body } = await getSubmissions();
      if (status === 200 && Array.isArray(body)) {
        const sortedSubmissions = body
          .sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          )
          .slice(0, 5);
        setRecentSubmissions(sortedSubmissions);
      }
    } catch (err) {}
  };

  useEffect(() => {
    fetchStats();
    fetchRecentSubmissions();
  }, [selectedMonth, selectedYear]);

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setSelectedMonth(Number(e.target.value));
  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setSelectedYear(Number(e.target.value));

  // Préparation des datas pour les graphiques
  // Bar chart : jours du mois
  const barLabels = stats ? Object.keys(stats.dailyStats) : [];
  const barData = stats ? Object.values(stats.dailyStats) : [];

  // Pie chart : statuts
  const pieLabels = ["En attente", "En cours", "Résolues", "Rejetées"];
  const pieKeys = [
    "in_standby",
    "in_processing",
    "resolved",
    "rejected",
  ] as const;
  const pieData = stats ? pieKeys.map((k) => stats.statusCount[k] || 0) : [];

  const monthName = new Date(0, selectedMonth - 1).toLocaleString("fr-FR", {
    month: "long",
  });

  return (
    <div className="w-full bg-white">
      <div className="w-full  py-8 space-y-8">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-4 md:space-y-0 bg-white p-6 rounded-lg shadow-md">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Tableau de Bord Administrateur
            </h1>
            <span className="text-lg text-gray-600">
              {monthName.charAt(0).toUpperCase() + monthName.slice(1)}{" "}
              {selectedYear}
            </span>
          </div>
          <div className="flex space-x-4">
            <div>
              <label className="block text-sm text-gray-700">Mois</label>
              <select
                value={selectedMonth}
                onChange={handleMonthChange}
                className="mt-1 p-2 border rounded-md"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(0, i).toLocaleString("fr-FR", { month: "long" })}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-700">Année</label>
              <select
                value={selectedYear}
                onChange={handleYearChange}
                className="mt-1 p-2 border rounded-md"
              >
                {availableYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {loading && <p className="text-center py-4">Chargement...</p>}
        {error && <p className="text-red-500 text-center py-4">{error}</p>}

        {stats && (
          <>
            {/* Résumé */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-bold text-gray-800 mb-2">Résumé</h2>
              <p className="text-gray-600">
                Aperçu global des soumissions et de la répartition par statut.
              </p>
            </div>

            {/* Statistiques principales */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                title="Total Soumissions"
                value={stats.totalCount}
                color="border-blue-500"
                icon={FileText}
              />
              <StatCard
                title="En attente"
                value={stats.statusCount.in_standby}
                color="border-yellow-500"
                icon={Clock}
              />
              <StatCard
                title="En cours"
                value={stats.statusCount.in_processing}
                color="border-orange-500"
                icon={UserCheck}
              />
              <StatCard
                title="Résolues"
                value={stats.statusCount.resolved}
                color="border-green-500"
                icon={CheckCircle}
              />
            </div>

            {/* Graphiques */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Diagramme en bande (bar chart) */}
              <div className="bg-white rounded-lg shadow-md p-6 flex flex-col transition-all duration-300 hover:shadow-lg">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-semibold text-gray-800">
                    Évolution des Soumissions
                  </h2>
                  <BarChart3 size={20} className="text-gray-400" />
                </div>
                <BarChart data={barData} labels={barLabels} />
              </div>
              {/* Diagramme circulaire */}
              <div className="bg-white rounded-lg shadow-md p-6 flex flex-col transition-all duration-300 hover:shadow-lg">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-semibold text-gray-800">
                    Répartition par Statut
                  </h2>
                  <PieIcon size={20} className="text-gray-400" />
                </div>
                <div className="flex flex-col md:flex-row items-center justify-around">
                  {pieData.length > 0 ? (
                    <>
                      <PieChart data={pieData} labels={pieLabels} />
                      <PieLegend data={pieData} labels={pieLabels} />
                    </>
                  ) : (
                    <div className="flex flex-col items-center text-gray-400 py-12">
                      <AlertCircle size={36} className="mb-4 text-gray-300" />
                      <p>Aucune donnée disponible.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Soumissions récentes */}
            <div className="bg-white rounded-lg shadow-md">
              <h2 className="text-lg font-semibold p-6 border-b">
                Soumissions Récentes
              </h2>
              <div className="divide-y">
                {recentSubmissions.length > 0 ? (
                  recentSubmissions.map((submission) => (
                    <div key={submission._id} className="p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="font-medium text-gray-900">
                            {submission.student.firstname}{" "}
                            {submission.student.lastname}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {submission.matiere} - {submission.submissionType}
                          </p>
                        </div>
                        <div className="text-sm text-gray-500">
                          {new Date(submission.createdAt).toLocaleDateString(
                            "fr-FR",
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="p-4 text-gray-500">Aucune soumission récente</p>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
